import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('profit_margin');
  const [sortOrder, setSortOrder] = useState('desc');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [buyOptions, setBuyOptions] = useState({});

  const API_BASE = window.location.origin;

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: '500'
      });
      if (category) {
        params.append('category', category);
      }

      const response = await fetch(`${API_BASE}/api/profit-analysis?${params}`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, category]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getProfitColor = (margin) => {
    if (margin >= 20) return '#27ae60';
    if (margin >= 10) return '#f39c12';
    if (margin >= 0) return '#e74c3c';
    return '#c0392b';
  };

  const fetchBuyOptions = async (itemId) => {
    if (buyOptions[itemId]) {
      // Already fetched
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/items/${itemId}/buy-options`);
      const data = await response.json();
      setBuyOptions(prev => ({
        ...prev,
        [itemId]: data.buy_options
      }));
    } catch (error) {
      console.error('Error fetching buy options:', error);
    }
  };

  const toggleItemExpanded = (itemId) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
    } else {
      setExpandedItem(itemId);
      fetchBuyOptions(itemId);
    }
  };

  return (
    <div className="App">
      <header className="header">
        <h1>🐉 DragonsCavern</h1>
        <p className="subtitle">Buy Low, Trade-In High - Guaranteed Profit Opportunities</p>
      </header>

      <div className="controls">
        <div className="filter-group">
          <label>Category:</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Sort By:</label>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [newSort, newOrder] = e.target.value.split('-');
              setSortBy(newSort);
              setSortOrder(newOrder);
            }}
          >
            <option value="profit_margin-desc">💰 Highest Profit</option>
            <option value="profit_percentage-desc">📈 Best ROI %</option>
            <option value="buy_price-asc">🏷️ Cheapest to Buy</option>
            <option value="tradein_value-desc">💵 Highest Trade-In</option>
          </select>
        </div>

        <button className="refresh-btn" onClick={fetchItems}>
          🔄 Refresh Data
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading arbitrage opportunities...</div>
      ) : (
        <>
          {/* Book Condition Warning - Only show when Books category selected */}
          {category === 'Books' && (
            <div className="book-warning">
              <h3>⚠️ Book Buyback: Condition Requirements</h3>
              <p><strong>Click "BookScouter →" to compare 30+ book buyers (BooksRun, TextbookRush, etc.)</strong></p>
              <p><strong>Prices shown are based on BooksRun rates (one of the buyers that accepts highlighting)</strong></p>

              <div style={{background: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '2px solid #ffc107'}}>
                <h4 style={{margin: '0 0 10px 0', color: '#856404'}}>🔑 CRITICAL: Highlighting ≠ Physical Wear!</h4>
                <p style={{margin: '0', color: '#856404', lineHeight: '1.6'}}>
                  <strong>BooksRun's "Significant Wear" checkbox means HIGHLIGHTING/MARKINGS, NOT physical damage!</strong><br/>
                  • Book can be BRAND NEW with highlighting = "significant wear" ✓<br/>
                  • Book can be WORN with NO highlighting = NO "significant wear" ✓<br/>
                  • Prices below assume highlighting IS present (~20% discount applied)
                </p>
              </div>

              <div className="warning-columns">
                <div className="warning-accepted">
                  <h4>✅ WHAT PRICES ASSUME:</h4>
                  <ul>
                    <li><strong>WITH highlighting/underlining</strong> (any amount)</li>
                    <li>Physical condition: Good to Like New OK</li>
                    <li>No water damage or torn pages</li>
                    <li>Intact binding</li>
                    <li>US edition with original ISBN</li>
                  </ul>
                  <p style={{marginTop: '10px', fontWeight: 'bold', color: '#155724'}}>
                    ⬆️ Find book WITHOUT highlighting? Get ~20% MORE!
                  </p>
                </div>

                <div className="warning-rejected">
                  <h4>❌ ALWAYS REJECTED:</h4>
                  <ul>
                    <li>Water damage, stains, mold</li>
                    <li>Torn, missing, or loose pages</li>
                    <li>Damaged/broken binding</li>
                    <li>Instructor/International editions</li>
                    <li>Workbooks with filled answers</li>
                    <li>Exam/Promotional copies</li>
                    <li>"Not For Resale" markings</li>
                  </ul>
                </div>
              </div>

              <p className="warning-note">
                📝 <strong>BONUS TIP:</strong> Physical wear (bent cover, creases) is OK as long as no highlighting!
                A worn book with NO highlighting gets the HIGHER price.
                A perfect book WITH highlighting gets the LOWER price (shown on DragonsCavern).
              </p>
            </div>
          )}

          <div className="stats">
            <div className="stat-card">
              <div className="stat-value">{items.length}</div>
              <div className="stat-label">Items Tracked</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {items.filter(i => i.profit_margin > 0).length}
              </div>
              <div className="stat-label">Profitable Items</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatCurrency(
                  items.length > 0
                    ? items.reduce((sum, i) => sum + i.profit_margin, 0) / items.length
                    : 0
                )}
              </div>
              <div className="stat-label">Avg Profit</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatCurrency(
                  items.length > 0
                    ? items.reduce((sum, i) => sum + i.profit_margin, 0)
                    : 0
                )}
              </div>
              <div className="stat-label">Total Profit</div>
            </div>
          </div>

          <div className="table-container">
            <table className="profit-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('buy_price')}
                  >
                    Buy Price {getSortIcon('buy_price')}
                  </th>
                  <th>Where to Buy</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('tradein_value')}
                  >
                    Trade-In Value {getSortIcon('tradein_value')}
                  </th>
                  <th>Trade-In Store</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('profit_margin')}
                  >
                    Profit {getSortIcon('profit_margin')}
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('profit_percentage')}
                  >
                    ROI % {getSortIcon('profit_percentage')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <React.Fragment key={item.item_id}>
                    <tr
                      className={expandedItem === item.item_id ? 'expanded-row' : ''}
                      onClick={() => toggleItemExpanded(item.item_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="item-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="expand-icon">
                            {expandedItem === item.item_id ? '▼' : '▶'}
                          </span>
                          {item.image_url && (
                            <img
                              src={item.image_url}
                              alt={item.item_name}
                              className="item-image"
                            />
                          )}
                          <div>
                            <div className="name">
                              {item.item_name}
                              {item.category === 'Books' && item.model && (
                                <span className={`book-format format-${item.model.toLowerCase()}`}>
                                  {item.model}
                                </span>
                              )}
                            </div>
                            <div className="condition">{item.condition}</div>
                            {item.isbn && (
                              <div className="model-number">ISBN: {item.isbn}</div>
                            )}
                            {item.model && item.category !== 'Books' && (
                              <div className="model-number">Model: {item.model}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{item.category}</td>
                      <td className="price">
                        <div className="price-breakdown">
                          <div>{formatCurrency(item.item_price)}</div>
                          {item.shipping_cost > 0 && (
                            <div className="shipping-cost">+${item.shipping_cost.toFixed(2)} ship</div>
                          )}
                          <div className="total-price">= {formatCurrency(item.lowest_buy_price)}</div>
                        </div>
                      </td>
                      <td className="platform">{item.cheapest_marketplace}</td>
                      <td className="price">
                        {formatCurrency(item.best_tradein_value)}
                      </td>
                      <td className="store">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.stopPropagation();

                            // For books with ISBN, go directly to BookScouter
                            if (item.category === 'Books' && item.isbn && item.best_tradein_store === 'BookScouter') {
                              window.open(`https://bookscouter.com/sell/${item.isbn}`, '_blank');
                              return;
                            }

                            const storeUrls = {
                              'GameStop': 'https://www.gamestop.com/trade',
                              'Gazelle': 'https://www.gazelle.com',
                              'Amazon Trade-In': 'https://www.amazon.com/tradein',
                              'KEH Camera': 'https://www.keh.com/sell',
                              'Guitar Center': 'https://www.guitarcenter.com/used',
                              "Bob's Watches": 'https://www.bobswatches.com',
                              'Apple Trade-In': 'https://www.apple.com/shop/trade-in',
                              'Rebag': 'https://www.rebag.com',
                              'Amoeba Music': 'https://www.amoeba.com/sell',
                              'TCGPlayer Buylist': 'https://www.tcgplayer.com/buylist',
                              'Card Kingdom': 'https://www.cardkingdom.com/purchasing',
                              'Bricks & Minifigs': 'https://www.bricksandminifigs.com',
                              'SecondSpin': 'https://www.secondspin.com',
                              'Local Pawn Shops': 'https://www.google.com/maps/search/pawn+shops',
                              'BookScouter': 'https://www.bookscouter.com',
                              'TextbookRush': 'https://www.textbookrush.com',
                              'BooksRun': 'https://www.booksrun.com'
                            };
                            const url = storeUrls[item.best_tradein_store];
                            if (url) window.open(url, '_blank');
                          }}
                          style={{ color: '#2a5298', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {item.best_tradein_store} →
                        </a>
                      </td>
                      <td
                        className="profit"
                        style={{ color: getProfitColor(item.profit_margin) }}
                      >
                        {formatCurrency(item.profit_margin)}
                      </td>
                      <td
                        className="percentage"
                        style={{ color: getProfitColor(item.profit_margin) }}
                      >
                        {item.profit_percentage}%
                      </td>
                    </tr>
                    {expandedItem === item.item_id && buyOptions[item.item_id] && (
                      <tr className="buy-options-row">
                        <td colSpan="8">
                          <div className="buy-options-container">
                            <h4>🛒 Where to Buy "{item.item_name}"</h4>
                            <div className="buy-options-list">
                              {buyOptions[item.item_id].map((option, idx) => (
                                <a
                                  key={idx}
                                  href={option.direct_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="buy-option-link"
                                >
                                  <div className="buy-option-platform">{option.platform}</div>
                                  <div className="buy-option-price">
                                    <span className="item-price-label">Item: {formatCurrency(option.item_price)}</span>
                                    {option.shipping_cost > 0 && (
                                      <span className="shipping-label">+ Ship: ${option.shipping_cost.toFixed(2)}</span>
                                    )}
                                    <strong className="total-label">Total: {formatCurrency(option.total_price)}</strong>
                                  </div>
                                  <div className="buy-option-action">Click to Search →</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-card-view">
            {items.map((item) => {
              const profitClass = item.profit_margin >= 20 ? '' :
                                 item.profit_margin >= 10 ? 'medium' : 'low';

              return (
                <div key={item.item_id} className="opportunity-card">
                  <div className="card-header">
                    <div className="card-title">
                      <h3>
                        {item.item_name}
                        {item.category === 'Books' && item.model && (
                          <span className={`book-format format-${item.model.toLowerCase()}`}>
                            {item.model}
                          </span>
                        )}
                      </h3>
                      <div className="card-category">{item.category}</div>
                      {item.isbn && (
                        <div className="card-model-number">ISBN: {item.isbn}</div>
                      )}
                      {item.model && item.category !== 'Books' && (
                        <div className="card-model-number">Model: {item.model}</div>
                      )}
                    </div>
                    <div className={`card-profit-badge ${profitClass}`}>
                      <span className="badge-profit">{formatCurrency(item.profit_margin)}</span>
                      <span className="badge-roi">{item.profit_percentage}% ROI</span>
                    </div>
                  </div>

                  <div className="card-details">
                    <div className="detail-item">
                      <div className="detail-label">💰 Buy For</div>
                      <div className="detail-value">
                        <div className="mobile-price-breakdown">
                          <span>Item: {formatCurrency(item.item_price)}</span>
                          {item.shipping_cost > 0 && (
                            <span className="mobile-shipping">+ Ship: ${item.shipping_cost.toFixed(2)}</span>
                          )}
                          <strong className="mobile-total">Total: {formatCurrency(item.lowest_buy_price)}</strong>
                        </div>
                      </div>
                      <div className="detail-sublabel">{item.cheapest_marketplace}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">🏪 Trade-In</div>
                      <div className="detail-value">{formatCurrency(item.best_tradein_value)}</div>
                      <div className="detail-sublabel">{item.best_tradein_store}</div>
                    </div>
                  </div>

                  <div className="card-action">
                    <button
                      className="action-btn btn-primary"
                      onClick={() => toggleItemExpanded(item.item_id)}
                    >
                      {expandedItem === item.item_id ? '▼ Hide Buy Options' : '🛒 Show Buy Options'}
                    </button>
                    <button
                      className="action-btn btn-secondary"
                      onClick={() => {
                        // For books with ISBN, go directly to BookScouter
                        if (item.category === 'Books' && item.isbn && item.best_tradein_store === 'BookScouter') {
                          window.open(`https://bookscouter.com/sell/${item.isbn}`, '_blank');
                          return;
                        }

                        const storeUrls = {
                          'GameStop': 'https://www.gamestop.com/trade',
                          'Gazelle': 'https://www.gazelle.com',
                          'Amazon Trade-In': 'https://www.amazon.com/tradein',
                          'BuyBackWorld': 'https://www.buybackworld.com',
                          'ItsWorthMore': 'https://www.itsworthmore.com',
                          'uSell': 'https://www.usell.com',
                          'Amoeba Music': 'https://www.amoeba.com/sell',
                          'Discogs Marketplace': 'https://www.discogs.com/sell',
                          'TCGPlayer Buylist': 'https://www.tcgplayer.com/buylist',
                          'Card Kingdom': 'https://www.cardkingdom.com/purchasing',
                          'Bricks & Minifigs': 'https://www.bricksandminifigs.com',
                          'KEH Camera': 'https://www.keh.com/sell',
                          'Guitar Center': 'https://www.guitarcenter.com/used',
                          "Bob's Watches": 'https://www.bobswatches.com',
                          'Apple Trade-In': 'https://www.apple.com/shop/trade-in',
                          'Local Pawn Shops': 'https://www.google.com/maps/search/pawn+shops',
                          'SecondSpin': 'https://www.secondspin.com',
                          'Rebag': 'https://www.rebag.com',
                          'BookScouter': 'https://www.bookscouter.com',
                          'TextbookRush': 'https://www.textbookrush.com',
                          'BooksRun': 'https://www.booksrun.com'
                        };
                        const url = storeUrls[item.best_tradein_store];
                        if (url) {
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      💵 Trade In at {item.best_tradein_store}
                    </button>
                  </div>

                  {/* Mobile Buy Options Dropdown */}
                  {expandedItem === item.item_id && buyOptions[item.item_id] && (
                    <div className="mobile-buy-options">
                      <h4>🛒 Where to Buy</h4>
                      <div className="mobile-buy-options-list">
                        {buyOptions[item.item_id].map((option, idx) => (
                          <a
                            key={idx}
                            href={option.direct_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mobile-buy-option"
                          >
                            <div className="mobile-option-header">
                              <strong>{option.platform}</strong>
                              <span className="mobile-option-total">{formatCurrency(option.total_price)}</span>
                            </div>
                            <div className="mobile-option-breakdown">
                              Item: {formatCurrency(option.item_price)}
                              {option.shipping_cost > 0 && ` + Ship: $${option.shipping_cost.toFixed(2)}`}
                            </div>
                            <div className="mobile-option-action">Tap to Search →</div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <footer className="footer">
        <p>
          Data updates every 12 hours • Prices are estimates based on market data
        </p>
      </footer>
    </div>
  );
}

export default App;
